import { Component, ErrorInfo, ReactNode } from "react";
import * as Sentry from "@sentry/react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Top-level React error boundary. Must be a class component — React does not
 * support getDerivedStateFromError / componentDidCatch in function components.
 *
 * Catches unhandled render/lifecycle errors anywhere in the tree and renders a
 * recoverable fallback instead of a blank white screen.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Report to Sentry (events are scrubbed in src/sentry.ts) with the React
    // component stack. No-op unless Sentry was initialized (VITE_SENTRY_DSN set).
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
    });
    // Also log to the browser console so developers can diagnose during development.
    console.error("[ErrorBoundary] Unhandled render error:", error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-canvas p-8 text-center">
          <h1 className="text-xl font-semibold text-fg">Something went wrong.</h1>
          <p className="text-sm text-fg-muted max-w-sm">
            An unexpected error occurred. Please refresh the page. If the problem
            persists, contact support.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="rounded-md bg-electric-blue px-4 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity"
          >
            Refresh page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
